import StudentCard from "../utils/cards/StudentCard";
import ParentCard from "../utils/cards/ParentCard";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { LoadingDots } from "utils/loadingAnimation";
import {
  ClassListType,
  ContactListType,
  ParentListType,
  StudentListType,
} from "utils/interfaces";
import EmptyView from "../utils/components/EmptyView";
import ClassCard from "../utils/cards/ClassCard";
import StaffCard from "../utils/cards/StaffCard";

interface ClassesListProps {
  activeClass: string;
  searchQuery: string;
  rolesToFilter: string[];
  classesToFilter: string[];
  classes: ClassListType[] | null;
  staff: ContactListType[] | null;
  students: StudentListType[] | null;
  parents: ParentListType[] | null;
  setShowTeachersList: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTeachers: React.Dispatch<React.SetStateAction<ContactListType[]>>;
  handleCardClick: (
    card: ClassListType | ContactListType | ParentListType | StudentListType
  ) => (event: React.MouseEvent) => void;
  handleDeleteClick: (
    record: ClassListType | ContactListType | StudentListType | ParentListType,
    itemType: string
  ) => (event: React.MouseEvent) => void;
  onCreateEditStudent: (title?: string, description?: string) => void;
  onCreateClass: () => void;
}

const ClassesList: React.FC<ClassesListProps> = ({
  activeClass,
  classes,
  staff,
  students,
  parents,
  searchQuery,
  rolesToFilter,
  classesToFilter,
  setShowTeachersList,
  setSelectedTeachers,
  handleCardClick,
  handleDeleteClick,
  onCreateEditStudent,
  onCreateClass,
}) => {
  // States definition
  const [previousList, setPreviousList] = useState(activeClass);

  /**
   * Define the previous list
   */
  useEffect(() => {
    setPreviousList(activeClass);
  }, [activeClass]);

  /**
   * Function that defines the criterion used for filtering
   * data by the search text
   * @param item
   * @returns
   */
  const textCriterion = (searchObjective: string) => {
    // And return the veredict
    return searchObjective.toLowerCase().includes(searchQuery.toLowerCase());
  };

  /**
   * Function that defines the criterion used for filtering
   * data by role
   * @param item 
   * @returns 
   */
  const roleCriterion = (item: ContactListType) => {
    return rolesToFilter.length > 0
      ? rolesToFilter.includes(item.role || "")
      : true;
  };

  /**
   * Function that defines the criterion used for filtering
   * data by checking if the item is inside the filtered
   * list of classes
   * @param item 
   * @returns 
   */
  const classCriterion = (item: ContactListType | StudentListType) => {
    return classesToFilter.length > 0
      ? item.classes &&
          item.classes.some((classItem: ClassListType | string) => {
            // Check the type of the class item
            if (typeof classItem === "string") {
              return classesToFilter.includes(classItem);
            }
            // Otherwise, return the _id
            return classesToFilter.includes(classItem._id);
          })
      : true;
  };

  /**
   * Function used to define the filter applied to the staff list
   * @param staff
   * @returns
   */
  const staffFilter = (staff: ContactListType) => {
    // Define the text to be seached
    const searchObjective = `${staff.firstName} ${staff.lastName} ${staff.email}`;
    // And define the text filter
    const textFilter = textCriterion(searchObjective);

    // Filter by role
    const roleFilter = roleCriterion(staff);

    // Filter by class
    const classFilter = classCriterion(staff)

    return textFilter && roleFilter && classFilter;
  };

  /**
   * Function used to define the filter applied to the staff list
   * @param staff
   * @returns
   */
  const studentFilter = (student: StudentListType) => {
    // Define the text to be seached
    const searchObjective = `${student.firstName} ${student.lastName} ${student.username}`;
    // And define the text filter
    const textFilter = textCriterion(searchObjective);

    // Filter by class
    const classFilter = classCriterion(student)

    return textFilter && classFilter;
  };

  return (
    <div className="relative w-full h-full">
      {/* Classes */}
      <motion.div
        initial={{
          x: "0%",
        }}
        animate={{
          x: activeClass === "classes" ? "0%" : "-100%",
          opacity: activeClass === "classes" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
      >
        {classes && classes.length > 0 ? (
          classes
            .filter((classItem: any) => {
              if (searchQuery.length === 0) return true;
              return classItem.name.toLowerCase().includes(searchQuery.toLowerCase());
            })
            .map((item: ClassListType, index: number) => (
              <ClassCard
                key={index}
                classItem={item}
                canEdit={true}
                handleCardClick={handleCardClick(item)}
                handleDelete={handleDeleteClick(item, "classes")}
                setShowTeachersList={setShowTeachersList}
                setSelectedTeachers={setSelectedTeachers}
              />
            ))
        ) : classes === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView
            showCreateNew={true}
            onCreateNewClick={() => onCreateClass()}
          />
        )}
      </motion.div>

      {/* Teachers */}
      <motion.div
        initial={{
          x: "100%",
        }}
        animate={{
          x:
            previousList === "classes"
              ? "100%"
              : activeClass === "staff"
              ? "0%"
              : "-100%",
          opacity: activeClass === "staff" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
      >
        {staff && staff.length > 0 ? (
          staff
            .filter(staffFilter)
            .map((staff: ContactListType, index: number) => (
              <StaffCard
                key={index}
                staff={staff}
                handleCardClick={handleCardClick(staff)}
                handleDelete={handleDeleteClick(staff, "staff")}
              />
            ))
        ) : staff === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView showCreateNew={false} />
        )}
      </motion.div>

      {/* Students */}
      <motion.div
        initial={{
          x: "100%",
        }}
        animate={{
          x:
            previousList === "parents"
              ? "-100%"
              : activeClass === "students"
              ? "0%"
              : "100%",
          opacity: activeClass === "students" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
      >
        {students && students.length > 0 ? (
          students
            .filter(studentFilter)
            .map((student: StudentListType, index: number) => (
              <StudentCard
                key={index}
                student={student}
                handleCardClick={handleCardClick(student)}
                handleDelete={handleDeleteClick(student, "students")}
              />
            ))
        ) : students === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView
            showCreateNew={true}
            onCreateNewClick={() => onCreateEditStudent()}
          />
        )}
      </motion.div>

      {/* Parents */}
      <motion.div
        initial={{
          x: "100%",
        }}
        animate={{
          x: activeClass === "parents" ? "0%" : "100%",
          opacity: activeClass === "parents" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
      >
        {parents && parents.length > 0 ? (
          parents
            .filter((parent: ParentListType) => {
              let name = parent.firstName + " " + parent.lastName;
              return name.toLowerCase().includes(searchQuery.toLowerCase());
            })
            .map((parent: ParentListType, index: number) => (
              <ParentCard
                key={index}
                parent={parent}
                handleCardClick={handleCardClick(parent)}
              />
            ))
        ) : parents === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView showCreateNew={false} />
        )}
      </motion.div>
    </div>
  );
};

export default ClassesList;
